/**
 * ResponsiveControls — Responsive dimension and direction controls.
 * Used by QuickPropertiesPanel for breakpoint-aware property editing.
 * Extracted from PageBuilder.js for modularity.
 */

import React, { useState } from 'react';

// Responsive Dimension Control Component
// Handles width/maxWidth controls with breakpoint support
export const ResponsiveDimensionControl = ({ field, currentValue, columns, selectedColumn, updateProperty }) => {
  const [activeBreakpoint, setActiveBreakpoint] = useState('base');
  
  // Get current responsive dimension value
  let responsiveObj;

  // Initialize responsive object structure
  if (typeof currentValue === 'object' && currentValue !== null) {
    // Check if it's already in responsive format
    if (currentValue.base !== undefined) {
      responsiveObj = {
        base: currentValue.base || { value: 'auto', unit: 'px' },
        tablet: currentValue.tablet || null,
        desktop: currentValue.desktop || null
      };
    } else {
      // Legacy format - convert to responsive
      responsiveObj = {
        base: {
          value: currentValue.value ?? 'auto',
          unit: currentValue.unit || 'px'
        },
        tablet: null,
        desktop: null
      };
    }
  } else {
    // Use default from field
    responsiveObj = field.default || {
      base: { value: 'auto', unit: 'px' },
      tablet: null,
      desktop: null
    };
  }


  // TODO: Check if we should just import from BreakpointToggle.js
  const breakpoints = [
    { id: 'base', label: 'Mobile' },
    { id: 'tablet', label: 'Tablet' },
    { id: 'desktop', label: 'Desktop' }
  ];

  // Get the current dimension for active breakpoint (with fallback to base)
  const currentDimension = responsiveObj[activeBreakpoint] || responsiveObj.base || { value: 'auto', unit: 'px' };
  const isAuto = currentDimension.value === 'auto';

  const updateResponsiveDimension = (updates) => {
    const updatedColumns = [...columns];
    const newDimension = { ...currentDimension, ...updates };

    const newResponsiveObj = {
      ...responsiveObj,
      [activeBreakpoint]: newDimension
    };

    updatedColumns[selectedColumn.columnIndex] = {
      ...updatedColumns[selectedColumn.columnIndex],
      [field.key]: newResponsiveObj
    };

    updateProperty('actualColumns', updatedColumns);
  };

  const clearOverride = () => {
    if (activeBreakpoint !== 'base') {
      const updatedColumns = [...columns];
      const newResponsiveObj = {
        ...responsiveObj,
        [activeBreakpoint]: null
      };

      updatedColumns[selectedColumn.columnIndex] = {
        ...updatedColumns[selectedColumn.columnIndex],
        [field.key]: newResponsiveObj
      };

      updateProperty('actualColumns', updatedColumns);
    }
  };

  const toggleAuto = () => {
    updateResponsiveDimension({ value: isAuto ? 0 : 'auto' });
  };

  return (
    <div className="space-y-3">
      {/* Breakpoint selector tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded">
        {breakpoints.map(bp => (
          <button
            key={bp.id}
            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
              activeBreakpoint === bp.id 
                ? 'bg-blue-500 text-white font-medium' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setActiveBreakpoint(bp.id)}
          >
            {bp.label}
            {/* Show dot indicator if this breakpoint has an override */}
            {responsiveObj[bp.id] && bp.id !== 'base' && (
              <span className="ml-1 inline-block w-1.5 h-1.5 bg-blue-300 rounded-full"></span>
            )}
          </button>
        ))}
      </div>

      {/* Auto checkbox */}
      <label className="flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={isAuto}
          onChange={toggleAuto}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="ml-2 text-xs text-gray-700">Auto (no constraint)</span>
      </label>

      {/* Value input - disabled when auto */}
      {!isAuto && (
        <>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Value</label>
            <input
              type="number"
              value={currentDimension.value}
              onChange={(e) => updateResponsiveDimension({ value: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="0"
              step="1"
              min="0"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Unit</label>
            <select
              value={currentDimension.unit}
              onChange={(e) => updateResponsiveDimension({ unit: e.target.value })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="px">px - Pixels</option>
              <option value="%">% - Percentage</option>
              <option value="rem">rem - Root EM</option>
              <option value="em">em - EM</option>
              <option value="vh">vh - Viewport Height</option>
              <option value="vw">vw - Viewport Width</option>
            </select>
          </div>

          {/* Preview/hint */}
          <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
            Preview: {currentDimension.value}{currentDimension.unit}
          </div>
        </>
      )}

      {/* Clear override button for non-base breakpoints */}
      {activeBreakpoint !== 'base' && responsiveObj[activeBreakpoint] && (
        <button
          onClick={clearOverride}
          className="w-full text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
        >
          Clear {activeBreakpoint} override (inherit from base)
        </button>
      )}

      {/* Info text for inheritance */}
      {activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint] && (
        <div className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
          ℹ️ Currently inheriting from Mobile (base)
        </div>
      )}
    </div>
  );
};

// Responsive Direction Control Component
// Handles direction controls with breakpoint support (base/tablet/desktop)

export const ResponsiveDirectionControl = ({ field, currentValue, columns, selectedColumn, updateProperty }) => {
  const [activeBreakpoint, setActiveBreakpoint] = useState('base');
  
  // Helper function to get human-readable direction label
  const getDirectionLabel = (direction) => {
    return direction === 'horizontal' ? 'Horizontal (Side by Side)' : 'Vertical (Stacked)';
  };
  
  // Get current responsive direction value
  let responsiveObj;

  // Initialize responsive object structure
  if (typeof currentValue === 'object' && currentValue !== null) {
    // Check if it's already in responsive format
    if (currentValue.base !== undefined) {
      responsiveObj = {
        base: currentValue.base || 'horizontal',
        tablet: currentValue.tablet || null,
        desktop: currentValue.desktop || null
      };
    } else {
      // Not in responsive format but is an object - shouldn't happen but handle it
      console.warn('ResponsiveDirectionControl: Unexpected direction format (object without base property), defaulting to horizontal', currentValue);
      responsiveObj = {
        base: 'horizontal',
        tablet: null,
        desktop: null
      };
    }
  } else if (typeof currentValue === 'string') {
    // Legacy format - convert string to responsive object
    responsiveObj = {
      base: currentValue,
      tablet: null,
      desktop: null
    };
  } else {
    // Use default from field
    responsiveObj = field.default || {
      base: 'horizontal',
      tablet: null,
      desktop: null
    };
  }

  const breakpoints = [
    { id: 'base', label: 'Mobile' },
    { id: 'tablet', label: 'Tablet' },
    { id: 'desktop', label: 'Desktop' }
  ];

  // Get the current direction for active breakpoint (with fallback to base)
  const currentDirection = responsiveObj[activeBreakpoint] || responsiveObj.base || 'horizontal';

  const updateResponsiveDirection = (newDirection) => {
    const updatedColumns = [...columns];

    const newResponsiveObj = {
      ...responsiveObj,
      [activeBreakpoint]: newDirection
    };

    updatedColumns[selectedColumn.columnIndex] = {
      ...updatedColumns[selectedColumn.columnIndex],
      [field.key]: newResponsiveObj
    };

    updateProperty('actualColumns', updatedColumns);
  };

  const clearOverride = () => {
    if (activeBreakpoint !== 'base') {
      const updatedColumns = [...columns];
      const newResponsiveObj = {
        ...responsiveObj,
        [activeBreakpoint]: null
      };

      updatedColumns[selectedColumn.columnIndex] = {
        ...updatedColumns[selectedColumn.columnIndex],
        [field.key]: newResponsiveObj
      };

      updateProperty('actualColumns', updatedColumns);
    }
  };

  return (
    <div className="space-y-3">
      {/* Breakpoint selector tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded">
        {breakpoints.map(bp => (
          <button
            key={bp.id}
            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
              activeBreakpoint === bp.id 
                ? 'bg-blue-500 text-white font-medium' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setActiveBreakpoint(bp.id)}
          >
            {bp.label}
            {/* Show dot indicator if this breakpoint has an override */}
            {responsiveObj[bp.id] && bp.id !== 'base' && (
              <span className="ml-1 inline-block w-1.5 h-1.5 bg-blue-300 rounded-full"></span>
            )}
          </button>
        ))}
      </div>

      {/* Direction selector buttons */}
      <div className="space-y-2">
        <label className="block text-xs text-gray-600 mb-1">Layout Direction</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => updateResponsiveDirection('horizontal')}
            className={`px-3 py-2 text-xs border rounded transition-colors ${
              currentDirection === 'horizontal'
                ? 'bg-blue-500 text-white border-blue-600 font-medium'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex flex-col items-center">
              <div className="mb-1">↔️</div>
              <div>Horizontal</div>
              <div className="text-xs opacity-75">(Side by Side)</div>
            </div>
          </button>
          <button
            onClick={() => updateResponsiveDirection('vertical')}
            className={`px-3 py-2 text-xs border rounded transition-colors ${
              currentDirection === 'vertical'
                ? 'bg-blue-500 text-white border-blue-600 font-medium'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex flex-col items-center">
              <div className="mb-1">↕️</div>
              <div>Vertical</div>
              <div className="text-xs opacity-75">(Stacked)</div>
            </div>
          </button>
        </div>
      </div>

      {/* Clear override button for non-base breakpoints */}
      {activeBreakpoint !== 'base' && responsiveObj[activeBreakpoint] && (
        <button
          onClick={clearOverride}
          className="w-full text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
        >
          Clear {activeBreakpoint} override (inherit from Mobile)
        </button>
      )}

      {/* Info text for inheritance */}
      {activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint] && (
        <div className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
          ℹ️ Currently inheriting from Mobile (base): {getDirectionLabel(responsiveObj.base)}
        </div>
      )}
    </div>
  );
};
